import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Linking,
  Image,
  Alert,
  Modal,
  TextInput,
  Switch,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Feather from 'react-native-vector-icons/Feather';

const AccountSettingsPage = ({ navigation }) => {
  const [userData, setUserData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [accountAdminValue, setAccountAdminValue] = useState('');

  // ✅ NEW: email update modal state
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [newEmailValue, setNewEmailValue] = useState('');
  const [sendConfirmEmail, setSendConfirmEmail] = useState(true);
  const [updatingEmail, setUpdatingEmail] = useState(false);

  const handleBackPress = () => {
    navigation.goBack();
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const updateAccountAdmin = () => {
    fetch('https://collabbayadmin.com/APIControllers/UpdateAccountAdmin.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `AccountAdmin=${encodeURIComponent(accountAdminValue)}`,
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          alert('Admin succesfully added');
          window.location.href = 'https://collabbayadmin.com/';
        } else {
          alert('Something went wrong, reload the page and try again' || 'Failed to update Account Admin.');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        Alert.alert('Error', 'Failed to update Account Admin.');
      });
  };

  const [accountAdmin, setAccountAdmin] = useState('');

  const fetchAccountAdmin = () => {
    fetch('https://collabbayadmin.com/APIControllers/GetVenueAccountAdmin.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setAccountAdmin(data.AccountAdmin);
        } else {
          console.error('Error fetching AccountAdmin:', data.message);
        }
      })
      .catch((error) => {
        console.error('Error fetching AccountAdmin:', error);
      });
  };

  useEffect(() => {
    fetchAccountAdmin();
  }, []);

  const fetchUserData = () => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setUserData(data);
      })
      .catch((error) => {
        console.error('Error fetching user data: ', error);
      });
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('https://collabbayadmin.com/APIControllers/EndSession.php', {
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        Alert.alert('Logout', 'Logout successful');
      } else {
        Alert.alert('Logout', 'No active session found');
      }

      window.location.href = 'https://collabbayadmin.com/';
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // ✅ NEW: helpers for email update
  const isValidEmail = (email) => {
    const e = String(email || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(e);
  };

  const openEmailModal = () => {
    setNewEmailValue((userData.UserEmail || '').trim());
    setSendConfirmEmail(true);
    setEmailModalVisible(true);
  };

  const submitEmailUpdate = async () => {
    const trimmed = (newEmailValue || '').trim();

    if (!isValidEmail(trimmed)) {
      Alert.alert('Invalid Email', 'Please enter a valid email address.');
      return;
    }

    if (updatingEmail) return;

    setUpdatingEmail(true);

    try {
      const res = await fetch('https://collabbayadmin.com/APIControllers/BrandUpdateEmail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          newEmail: trimmed,
          sendConfirmation: sendConfirmEmail,
        }),
      });

      const data = await res.json();

      if (data.success) {
        // Update local UI immediately
        setUserData((prev) => ({
          ...prev,
          UserEmail: data.updatedEmail || trimmed,
        }));

        setEmailModalVisible(false);

        Alert.alert(
          'Email Updated',
          data.emailSent
            ? 'Your email has been updated and a confirmation email was sent.'
            : 'Your email has been updated.'
        );
      } else {
        Alert.alert('Update Failed', data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to update email. Please try again.');
    } finally {
      setUpdatingEmail(false);
    }
  };

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff' }}>
      <View style={[styles.container, { width: '100%', maxWidth: 600, alignSelf: 'center', backgroundColor: '#fff', flex: 1 }]}>
        <View style={styles.backContainer}>
          <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
            <Ionicons name="arrow-back" size={24} color="black" />
          </TouchableOpacity>
        </View>

        {/* Profile Section */}
        <View style={styles.profileSection}>
          <Image style={styles.profilePicture} source={{ uri: userData.ProfileImage }} resizeMode="cover" />
          <View style={styles.profileDetails}>
            {/* ✅ UPDATED: email row now includes a small edit button */}
            <View style={styles.emailContainer}>
              <Ionicons name="mail-outline" size={20} color="black" />
              <Text style={[styles.profileText, { flex: 1 }]} numberOfLines={1}>
                {userData.UserEmail}
              </Text>

              <TouchableOpacity style={styles.editEmailButton} onPress={openEmailModal}>
                <Ionicons name="pencil" size={16} color="black" />
              </TouchableOpacity>
            </View>

            <View style={styles.businessNameContainer}>
              <Ionicons name="business-outline" size={20} color="black" />
              <Text style={styles.profileText}>{userData.BusinessName}</Text>
            </View>

            <View style={styles.businessAddressContainer}>
              <Ionicons name="location-outline" size={20} color="black" />
              <Text style={styles.profileText}>{userData.BusinessAddress}</Text>
            </View>
          </View>
        </View>

        <TouchableOpacity
          style={styles.settingContainer}
          onPress={() => Linking.openURL('https://collabbayadmin.com/UpdateProfile.html')}
        >
          <Ionicons name="person-outline" size={24} color="black" />
          <Text style={styles.settingText}>Edit My Logo</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingContainer}>
          <Ionicons name="notifications-outline" size={24} color="black" />
          <Text style={styles.settingText}>Help</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingContainer} onPress={() => Linking.openURL('https://collabbayadmin.com/planchoice')}>
          <Ionicons name="card-outline" size={24} color="black" />
          <Text style={styles.settingText}>Billing</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingContainer} onPress={() => Linking.openURL('https://collabbayadmin.com/AddClient')}>
          <Ionicons name="briefcase-outline" size={24} color="black" />
          <Text style={styles.settingText}>Agency Client Manager</Text>
        </TouchableOpacity>


        <TouchableOpacity
          activeOpacity={0.9}
          onPress={() => navigation.navigate('AssignVouchers')}
          style={{
            width: '100%',
            marginTop: -8,
            marginBottom: 20,
            paddingVertical: 15,
            paddingHorizontal: 16,
            backgroundColor: '#111111',
            borderRadius: 18,
            borderWidth: 1,
            borderColor: '#252525',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <View
              style={{
                width: 42,
                height: 42,
                borderRadius: 13,
                backgroundColor: '#FDF05D',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ionicons name="wallet-outline" size={21} color="#111111" />
            </View>

            <View style={{ flex: 1, marginLeft: 13 }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 6,
                }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '700',
                    color: '#ffffff',
                  }}
                >
                  Distribute Vouchers
                </Text>

                <View
                  style={{
                    backgroundColor: '#FDF05D',
                    borderRadius: 999,
                    paddingVertical: 3,
                    paddingHorizontal: 7,
                  }}
                >
                  <Text
                    style={{
                      color: '#111111',
                      fontSize: 9,
                      fontWeight: '900',
                      letterSpacing: 0.8,
                    }}
                  >
                    NEW
                  </Text>
                </View>

                <View
                  style={{
                    borderWidth: 1,
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderRadius: 999,
                    paddingVertical: 3,
                    paddingHorizontal: 7,
                  }}
                >
                  <Text
                    style={{
                      color: '#ffffff',
                      fontSize: 9,
                      fontWeight: '800',
                      letterSpacing: 0.8,
                    }}
                  >
                    BETA
                  </Text>
                </View>
              </View>

              <Text
                style={{
                  marginTop: 5,
                  fontSize: 11,
                  lineHeight: 15,
                  fontWeight: '500',
                  color: '#bdbdbd',
                }}
              >
                Give creators trackable vouchers to share
              </Text>
            </View>
          </View>

          <Ionicons
            name="chevron-forward"
            size={18}
            color="#FDF05D"
            style={{ marginLeft: 10 }}
          />
        </TouchableOpacity>

        <View>
          <Text style={styles.venueTextMessage}>Your venue code is</Text>
          <Text style={styles.venueText}>{userData.VenueCode}</Text>
        </View>

{userData.Username === 'CheZucconi' && (
          <TouchableOpacity style={styles.settingContainer} onPress={() => navigation.navigate('collabbayAnalytics')}>
            <Feather name="bar-chart" size={24} color="black" />
            <Text style={styles.settingText}>Analytics</Text>
          </TouchableOpacity>
        )}

        <View style={styles.accountAdminContainer}>
          <Ionicons name="person-outline" size={24} color="black" style={styles.icon} />
          <View style={styles.cardTextContainer}>
            <Text style={styles.accountAdminLabel}>Account Admin</Text>
            {accountAdmin ? (
              accountAdmin.split(',').map((item, index) => (
                <Text key={index} style={styles.accountAdminValue}>
                  {item.trim()}
                </Text>
              ))
            ) : (
              <Text style={styles.accountAdminValue}>None, add below</Text>
            )}
          </View>
        </View>

        {/* Existing Account Admin modal */}
        <Modal animationType="slide" transparent={true} visible={modalVisible} onRequestClose={() => setModalVisible(false)}>
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Text style={{ textAlign: 'center', fontSize: 18, fontWeight: 'bold', marginBottom: 15 }}>
                Account admins, such as your reservations or confirmations team, are notified of approved bookings. Add their email as an account admin to keep them in the loop.
              </Text>

              <TextInput
                style={styles.input}
                placeholder="i.e. team@yourvenue.com"
                value={accountAdminValue}
                onChangeText={(text) => {
                  const noSpacesText = text.replace(/\s/g, '');
                  setAccountAdminValue(noSpacesText);
                }}
                onBlur={() => {
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (accountAdminValue && !emailRegex.test(accountAdminValue)) {
                    alert('Invalid Email', 'Please enter a valid email address.');
                  }
                }}
              />

              <TouchableOpacity style={styles.saveButton} onPress={updateAccountAdmin}>
                <Text style={styles.saveButtonText}>Add Admin</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.cancelButton} onPress={() => setModalVisible(false)}>
                <Text style={styles.cancelButtonText}>X</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        <TouchableOpacity
          style={[styles.settingContainer, { marginTop: 10, justifyContent: 'center', alignItems: 'center' }]}
          onPress={() => setModalVisible(true)}
        >
          <Text style={[styles.settingText, { fontSize: 12, fontWeight: 'bold' }]}>Update or add an account admin</Text>
        </TouchableOpacity>

        {/* ✅ NEW: Email Update modal */}
        <Modal
          animationType="slide"
          transparent={true}
          visible={emailModalVisible}
          onRequestClose={() => setEmailModalVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Text style={styles.emailModalTitle}>Update Email Address</Text>

              <Text style={styles.emailWarningText}>
                Are you sure you want to update your email address?
                {'\n\n'}
                All bookings will now be sent to this address, so ensure you have access to it to avoid missing anything important.
              </Text>

              <TextInput
                style={styles.input}
                placeholder="your@email.com"
                value={newEmailValue}
                onChangeText={(text) => setNewEmailValue(text.replace(/\s/g, ''))}
                autoCapitalize="none"
                keyboardType="email-address"
              />

              <View style={styles.confirmRow}>
                <Text style={styles.confirmLabel}>Send me a confirmation email (recommended)</Text>
                <Switch value={sendConfirmEmail} onValueChange={setSendConfirmEmail} />
              </View>

              <TouchableOpacity
                style={[styles.saveButton, updatingEmail ? { opacity: 0.7 } : null]}
                onPress={submitEmailUpdate}
                disabled={updatingEmail}
              >
                {updatingEmail ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <ActivityIndicator color="#fff" />
                    <Text style={styles.saveButtonText}>Updating...</Text>
                  </View>
                ) : (
                  <Text style={styles.saveButtonText}>Update Email</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity style={styles.cancelButton} onPress={() => setEmailModalVisible(false)} disabled={updatingEmail}>
                <Text style={styles.cancelButtonText}>X</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        <TouchableOpacity style={styles.homeButton} onPress={() => navigation.navigate('BusinessHomepage')}>
          <Feather name="home" size={24} color="#000" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
          <Text style={styles.logoutText}>Log Out</Text>
        </TouchableOpacity>

        <TouchableOpacity onPress={() => Linking.openURL('https://collabbay.com/PrivacyPolicy.html')}>
          <Text style={styles.linkText}>By using Collabbay, you agree to our Privacy Policy.</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 30,
    color: '#333333',
  },
  profileSection: {
    alignItems: 'center',
    marginBottom: 30,
  },
  profilePicture: {
    width: 150,
    height: 150,
    borderRadius: 75,
    borderWidth: 5,
    borderColor: '#fff',
  },
  profileDetails: {
    alignItems: 'center',
    marginTop: 10,
    width: '100%',
  },
emailContainer: {
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: 10,
  width: '100%',
  backgroundColor: '#f3f3f3',
  paddingVertical: 10,
  paddingHorizontal: 12,
  borderRadius: 16,
},

  businessNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
  },
  businessAddressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
  },
  profileText: {
    marginLeft: 10,
    fontSize: 18,
    color: '#333333',
  },

  // ✅ NEW: small edit button next to email
editEmailButton: {
  marginLeft: 10,
  width: 30,
  height: 30,
  borderRadius: 15,
  backgroundColor: '#fff',
  borderWidth: 1,
  borderColor: '#e5e5e5',
  alignItems: 'center',
  justifyContent: 'center',
},

  venueText: {
    fontSize: 57,
    color: '#333333',
    padding: 15,
    backgroundColo: '#f3f3f3',
    textAlign: 'center',
    borderRadius: 15,
  },
  venueTextMessage: {
    fontSize: 12,
    color: '#333333',
    padding: 15,
    backgroundColo: '#f3f3f3',
    textAlign: 'center',
    borderRadius: 15,
  },
  settingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#f3f3f3',
    borderRadius: 15,
    width: '100%',
  },
  settingText: {
    marginLeft: 20,
    fontSize: 18,
    color: '#333333',
  },
  logoutButton: {
    backgroundColor: 'black',
    paddingVertical: 15,
    paddingHorizontal: 50,
    borderRadius: 15,
    marginTop: 30,
  },
  logoutText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  homeButton: {},
  linkText: {
    fontSize: 9,
    color: 'black',
    textDecorationLine: 'underline',
    marginTop: 25,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 10,
    width: '90%',
    alignItems: 'center',
  },
  modalHeader: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    width: '100%',
    padding: 10,
    borderRadius: 5,
    marginBottom: 15,
  },
  saveButton: {
    backgroundColor: 'black',
    padding: 10,
    borderRadius: 5,
    width: '100%',
    alignItems: 'center',
  },
  saveButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  cancelButton: {
    marginTop: 10,
    width: '100%',
    alignItems: 'center',
  },
  cancelButtonText: {
    color: 'red',
    fontWeight: 'bold',
  },
  accountAdminContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 20,
    padding: 15,
    width: '79%',
    backgroundColor: '#f9f9f9',
    borderRadius: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
    alignSelf: 'center',
  },
  accountAdminValue: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
    lineHeight: 20,
  },
  cardTextContainer: {
    flex: 1,
  },
  accountAdminLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  icon: {
    marginRight: 15,
    marginTop: 5,
  },
  backContainer: {
    alignItems: 'center',
    marginBottom: 20,
    marginTop: 0,
    padding: 20,
  },

  // ✅ NEW: email modal styling
  emailModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  emailWarningText: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    marginBottom: 15,
    lineHeight: 20,
  },
  confirmRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    gap: 12,
  },
  confirmLabel: {
    flex: 1,
    fontSize: 13,
    color: '#333',
  },
});

export default AccountSettingsPage;
